// Streaming .goalhorns writer. Zip layout matches the desktop exporter:
// manifest.json first, then photos/ and songs/ entries in cell order.
//
// Compression: client-zip only writes STORED entries (it has no deflate support), so the
// manifest is stored too, not deflated as the desktop does. The desktop importer reads
// both methods through zipfile, so this is accepted (see test/compat.test.ts).

import { makeZip } from "client-zip";

import { MANIFEST_NAME } from "./constants";
import { buildManifest, formatExportedAt, manifestBytes } from "./manifest";
import { planCells, type MediaRef, type PlannedCell } from "./plan";
import { parseBundleInput, type BundleInput } from "./schema";

/** Returns the bytes (or a stream of bytes) for a content-addressed media file. */
export type FetchMedia = (sha256: string) => Promise<ReadableStream<Uint8Array> | Uint8Array>;

interface ZipEntry {
  readonly name: string;
  readonly input: Uint8Array | ReadableStream<Uint8Array>;
  readonly lastModified: Date;
}

async function mediaEntry(
  media: MediaRef,
  fetchMedia: FetchMedia,
  lastModified: Date,
): Promise<ZipEntry> {
  return { name: media.entry, input: await fetchMedia(media.sha256), lastModified };
}

async function* zipEntries(
  manifest: Uint8Array,
  planned: readonly PlannedCell[],
  fetchMedia: FetchMedia,
  lastModified: Date,
): AsyncGenerator<ZipEntry> {
  yield { name: MANIFEST_NAME, input: manifest, lastModified };
  for (const cell of planned) {
    for (const media of [cell.photo, cell.song]) {
      if (media) yield await mediaEntry(media, fetchMedia, lastModified);
    }
  }
}

/**
 * Validate `input` and stream a .goalhorns zip. Media is pulled lazily, one entry at a time,
 * through `fetchMedia`, which is called once per entry and must return fresh content each time.
 * Throws a ZodError synchronously when the input does not match the desktop limits.
 */
export function writeBundle(
  input: BundleInput,
  fetchMedia: FetchMedia,
  now: Date = new Date(),
): ReadableStream<Uint8Array> {
  const valid = parseBundleInput(input);
  const manifest = buildManifest(valid, formatExportedAt(now));
  const planned = planCells(valid);
  return makeZip(zipEntries(manifestBytes(manifest), planned, fetchMedia, now));
}
